import PropTypes from 'prop-types';
import {action, autorun, observable, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import React, {Component} from 'react';
import forEachRight from 'lodash/forEachRight';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';

//helpers
import {convertFocusPointToCss, getFittingRect} from 'utils/image-utils';
import {initializeKonvaStage, initializeKonvaLayer, drawShape} from 'utils/overlay-shapes-utils';

//styles
import {Overlay, KonvaContainer} from './styles';

//constants
import {ASPECT_RATIO} from './constants';

@observer
class OverlayComponent extends Component {
  static propTypes = {
    imageAspectRatio: PropTypes.number.isRequired,
    imageFit: PropTypes.oneOf(['contain', 'cover']).isRequired,
    imageObjectPositionX: PropTypes.number.isRequired,
    imageObjectPositionY: PropTypes.number.isRequired
  };

  @observable width = null;
  @observable height = null;
  @observable x = null;
  @observable y = null;
  @observable.ref overlayShapes = this.props.overlayShapes;

  konvaStage = null;
  konvaLayer = null;
  konvaContainerRef = null;
  disposeUpdater = noop;

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    this.disposeUpdater = autorun(this.updateKonva);
  }

  componentDidUpdate(prevProps) {
    const {overlayShapes} = this.props;

    if (overlayShapes !== prevProps.overlayShapes) {
      this.setOverlayShapes(overlayShapes);
    }
  }

  componentWillUnmount() {
    if (this.konvaStage) {
      this.konvaStage.destroy();
    }

    this.disposeUpdater();
  }

  @action
  handleResize = ({width: containerWidth, height: containerHeight} = {}) => {
    const {imageAspectRatio, imageFit, imageObjectPositionX, imageObjectPositionY, imageMetadata} = this.props;

    // Fit image into container:
    const imageRect = getFittingRect(
      imageFit,
      imageObjectPositionX,
      imageObjectPositionY,
      containerWidth,
      containerHeight,
      imageAspectRatio
    );

    const {kx: overlayObjectPositionX, ky: overlayObjectPositionY} = convertFocusPointToCss(
      ASPECT_RATIO,
      imageAspectRatio,
      imageMetadata
    );

    // Fit overlay into image:
    const overlayRect = getFittingRect(
      imageFit === 'cover' ? 'contain' : 'cover',
      overlayObjectPositionX,
      overlayObjectPositionY,
      imageRect.width,
      imageRect.height,
      ASPECT_RATIO
    );

    this.width = overlayRect.width;
    this.height = overlayRect.height;
    this.x = imageRect.x + overlayRect.x;
    this.y = imageRect.y + overlayRect.y;
  };

  setKonvaContainerRef = konvaContainerRef => {
    this.konvaContainerRef = konvaContainerRef;
  };

  @action
  setOverlayShapes = overlayShapes => {
    this.overlayShapes = overlayShapes;
  };

  updateKonva = () => {
    const {height, overlayShapes, width} = this;

    if (!this.konvaContainerRef) {
      return;
    }

    if (this.konvaStage) {
      this.konvaStage.size({width, height});
    } else {
      this.konvaStage = initializeKonvaStage(this.konvaContainerRef, width, height);
    }

    if (this.konvaLayer) {
      this.konvaLayer.destroy();
    }

    if (isEmpty(overlayShapes)) {
      return;
    }

    this.konvaLayer = initializeKonvaLayer();
    this.konvaStage.add(this.konvaLayer);

    forEachRight(overlayShapes, shape => {
      const konvaShape = drawShape(shape, width, height);
      this.konvaLayer.add(konvaShape);
      this.konvaLayer.draw();
    });
  };

  render() {
    const {shouldShowOverlayShapes} = this.props;

    return (
      <Overlay onResize={this.handleResize}>
        {shouldShowOverlayShapes && (
          <KonvaContainer
            ref={this.setKonvaContainerRef}
            style={{
              left: this.x,
              top: this.y
            }}
          />
        )}
      </Overlay>
    );
  }
}

export default OverlayComponent;
